<script setup>
import {
    testLinkScheme,
    testInternalLink,
    testRelativeLink,
    formatUrl,
    checkActive,
    preserveUTMQueries,
} from '@/composables/helpers/links';

const props = defineProps({
    href: {
        type: String,
        required: true,
    },
    label: {
        type: String,
        required: false,
        default: false
    },
    replace: {
        type: Boolean,
        default: false,
    },
    button: {
        type: Object,
        default: null,
    },
    target: {
        type: [String, Boolean],
        default: false,
    },
    windowed: {
        type: Boolean,
        default: false,
    },
    external: {
        type: Boolean,
        default: false,
    },
});

const internal = ref(false);
const relative = ref(false);

const url = ref(props.href);
// Check if the link has a valid scheme, if so, we shouldn't open in a new tab
const validScheme = testLinkScheme(url.value);

// If the link is relative, it's internal
// Else we test if it's a valid URL, and then check if it's host is internal
relative.value = testRelativeLink(url.value);
if (relative.value) internal.value = true;
else internal.value = testInternalLink(url.value);

if (props.target) internal.value = false

// If the link is internal, we format it
if (internal.value) url.value = formatUrl(url.value, relative.value);

const replaceState = () => {
    history.replaceState({}, null, url.value);
};

const route = useRoute();
const active = ref(false);

const updateActive = () => {
    if (internal.value) active.value = checkActive(url.value, route.fullPath);
};

updateActive();

const openWindow = () => {
    window.open(url.value, '_blank', 'width=500,height=500');
};

const activeClass = computed(() => {
    if (active.value == 1) return 'mess-active-link';
    if (active.value == 2) return 'mess-current-active-link';
    return '';
});

useNuxtApp().hook('page:transition:finish', () => {
    updateActive();
});

const forceRefresh = computed(() => {
    // If the link is on the current page, and has parameters we should replace the state
    let hit = false;
    if (!url || !url.value) return false;
    const onCurrentPage = url.value.includes(route.path);
    // If we are on the current page, and the link has a query, we should force a refresh
    // EG: route.path = /agenda, url.value = /agenda?day=1 -> force refresh
    // EG: route.path = /agenda, url.value = /agenda -> no refresh
    if (onCurrentPage) hit = url.value.includes('?');
    // If we are on the current page, have params, and the link is the root page, we should force a refresh
    // EG: route.path = /agenda, url.value = / -> no refresh
    // EG: route.path = /agenda?day=1, url.value = /agenda -> force refresh
    if (onCurrentPage && !hit) {
        const hasParams = route.fullPath.includes('?');
        const isRootPage = route.path === url.value;
        hit = hasParams && isRootPage;
    }``
    return false;
});
</script>

<template>
    <a v-if="replace" :href="url" class="mess-link replace" @click.prevent="replaceState()" :aria-label='label' data-allow-mismatch>
        <slot />
    </a>
    <a v-else-if="forceRefresh && internal" :href="preserveUTMQueries(url)"
        data-allow-mismatch
        :aria-label='label'
        :class="`mess-link force-refresh internal-link ${activeClass}`">
        <slot />
    </a>
    <a v-else-if="windowed" class="mess-link window" :href="url" @click.prevent="openWindow()" :aria-label='label'  data-allow-mismatch>
        <slot />
    </a>
    <NuxtLink :to="preserveUTMQueries(url)" v-else-if="internal" :class="`mess-link internal-link ${activeClass}`" :aria-label='label'  data-allow-mismatch :external="external">
        <slot />
    </NuxtLink>
    <span v-else-if="!validScheme && !target">
        <slot />
    </span>
    <a v-else :href="url" :target="target ? target : '_blank'" class="mess-link external-link" :aria-label='label'  data-allow-mismatch>
        <slot />
        <span v-if="!target" class="sr-only">(opens in a new tab)</span>
    </a>
</template>
